/**
 * Contrato da aba de suporte (impersonação de owner pelo admin).
 *
 * O admin abre o app com `?impersonation=<session_id>` + o magic link do
 * owner. Essa aba precisa ser ISOLADA das sessões reais do owner e da sessão
 * do próprio admin no domínio:
 *   - a sessão de auth vai para `sessionStorage` (morre com a aba), na chave
 *     `IMPERSONATION_AUTH_STORAGE_KEY`, nunca no cookie compartilhado do
 *     domínio pai;
 *   - o id da sessão de impersonação fica em
 *     `sessionStorage[IMPERSONATION_STORAGE_FLAG]` — é o que liga o banner, o
 *     claim e o isolamento. Sobrevive a F5 na mesma aba.
 *
 * Doc: docs/funcionalidades/impersonacao-de-owner.md
 *
 * Tudo aqui é tolerante a SSR / sessionStorage indisponível (modo privado,
 * iframe bloqueado): na falta do storage, a aba simplesmente não é de suporte.
 */

/** Chave em sessionStorage com o id da sessão de impersonação desta aba. */
export const IMPERSONATION_STORAGE_FLAG = "pps-impersonation";

/** storageKey do client Supabase na aba de suporte (em sessionStorage). */
export const IMPERSONATION_AUTH_STORAGE_KEY = "sb-impersonation";

/** Query param que o admin-impersonate põe no redirect do magic link. */
const IMPERSONATION_QUERY_PARAM = "impersonation";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getSessionStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Lê `?impersonation=<uuid>` da URL atual e grava o id no sessionStorage da
 * aba. Chamar ANTES de criar o client Supabase (o storage depende disso).
 * Devolve o id capturado (ou o que já estava gravado nesta aba), ou null.
 */
export function captureImpersonationFromUrl(): string | null {
  let fromUrl: string | null = null;
  try {
    if (typeof window !== "undefined" && window.location) {
      const value = new URLSearchParams(window.location.search).get(
        IMPERSONATION_QUERY_PARAM,
      );
      if (value && UUID_RE.test(value)) fromUrl = value;
    }
  } catch {
    fromUrl = null;
  }

  if (fromUrl) {
    const storage = getSessionStorage();
    try {
      storage?.setItem(IMPERSONATION_STORAGE_FLAG, fromUrl);
    } catch {
      /* storage cheio/bloqueado: a aba segue sem isolamento persistido */
    }
    return fromUrl;
  }
  return getImpersonationSessionId();
}

/** Id da sessão de impersonação desta aba, ou null se não é aba de suporte. */
export function getImpersonationSessionId(): string | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    const value = storage.getItem(IMPERSONATION_STORAGE_FLAG);
    return value && UUID_RE.test(value) ? value : null;
  } catch {
    return null;
  }
}

/** true quando esta aba é uma aba de suporte (impersonação). */
export function isImpersonationTab(): boolean {
  return getImpersonationSessionId() !== null;
}

/**
 * Limpa a marca da aba e a sessão de auth guardada nela. Usado no fim da
 * sessão de suporte (banner), depois do signOut local.
 */
export function clearImpersonationTab(): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(IMPERSONATION_STORAGE_FLAG);
    storage.removeItem(IMPERSONATION_AUTH_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Storage de auth para a aba de suporte: sessionStorage puro, com fallback
 * em memória. Nunca escreve cookie nem localStorage.
 */
export function createImpersonationStorage(): {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
} {
  const memory = new Map<string, string>();
  return {
    getItem(key) {
      const storage = getSessionStorage();
      try {
        if (storage) return storage.getItem(key);
      } catch {
        /* cai na memória */
      }
      return memory.get(key) ?? null;
    },
    setItem(key, value) {
      const storage = getSessionStorage();
      try {
        if (storage) {
          storage.setItem(key, value);
          return;
        }
      } catch {
        /* cai na memória */
      }
      memory.set(key, value);
    },
    removeItem(key) {
      const storage = getSessionStorage();
      try {
        storage?.removeItem(key);
      } catch {
        /* noop */
      }
      memory.delete(key);
    },
  };
}
